/// <reference path="../../../app.definition.ts" />

import angular from "angular";
import * as pdfjsViewer from "pdfjs-dist/web/pdf_viewer.mjs";
import {PDFJS_CONFIG} from "@config/pdf.config";
import { _app, ensureOverlay, overlayManager, PDFRenderingQueue } from "./print.service";

export interface IPdfViewerService {
    init(): void;
    clearPdf(): void;
    loadPdf(obj: object, showPrintButton: boolean): boolean;

}

export class PdfViewerService implements IPdfViewerService {
    workerSrc: string;
    cmapDir: string;
    imageDir: string;
    disableWorker: boolean;
    verbosity: string;
    currentPdf: any;
    container: JQuery;
    printService;
    scale: string;
    src: string;
    pagesOverview;
    loaded: any;
    numLoaded: number;
    pdfViewer: any;
    pdfLinkService: any;

    initialized: boolean;
    pdfRenderingQueue;
    eventBus;
    static $inject = ["pdfjs"];
    constructor(
        private pdfjs
    ) {
        this.initialized = false;
        this.loaded = {};
        this.numLoaded = 0;
        this.currentPdf = null;
        this.src = null;
        this.container = null;
        this.pdfjs.GlobalWorkerOptions.workerSrc = './node_modules/pdfjs-dist/build/pdf.worker.min.js';
    }

    init = (): void => {
        if (angular.equals(this.pdfjs, {})) {
            console.error("PdfViewerService: Cannot initialize because PDF.js not loaded!")
            return;
        }

        !!this.workerSrc && (this.pdfjs.workerSrc = this.workerSrc);
        !!this.cmapDir && (this.pdfjs.cMapUrl = this.cmapDir);
        !!this.imageDir && (this.pdfjs.imageResourcesPath = this.imageDir);
        !!this.disableWorker && (this.pdfjs.this = true);

        this.container = angular.element('#pdf-container');
        if (this.verbosity !== null) {
            let level = this.verbosity;
            if (typeof level === 'string') level = this.pdfjs.VERBOSITY_LEVELS[level];
            this.pdfjs.verbosity = level;
        }

        this.pdfLinkService = new pdfjsViewer.PDFLinkService();
        this.pdfRenderingQueue = new PDFRenderingQueue();
        this.eventBus = new pdfjsViewer.EventBus();
        this.pdfViewer = new pdfjsViewer.PDFViewer({
            container: this.container[0] as HTMLDivElement,
            linkService: this.pdfLinkService,
            eventBus: this.eventBus
        });

        this.pdfLinkService.setViewer(this.pdfViewer);
        this.pdfRenderingQueue.setViewer(this.pdfViewer);
        this.pdfRenderingQueue.onIdle = () => {
            if (!this.currentPdf) {
                return;
            }
            this.pdfViewer.cleanup();
        };


        // // (Optionally) enable find controller.
        // const pdfFindController = new this.pdfjs.PDFFindController({
        //     pdfViewer: pdfViewer
        // });
        //
        // pdfViewer.setFindController(pdfFindController);

    }

    clearPdf = (): void => {
        this.loaded = {};
        this.numLoaded = 0;
        this.src = null;
        this.currentPdf = null;
        this.pdfViewer.setDocument(null);
        this.pdfLinkService.setDocument(null, null);
        angular.element('body').css('overflow', 'auto');
        this.container.removeClass('pdf-loaded');
        if (this.printService) {
            this.printService.destroy();
            this.printService = null;
        }
        this.forceRendering();
    }

    forceRendering = () => {
        this.pdfRenderingQueue.printing = true;
        this.pdfRenderingQueue.isThumbnailViewEnabled = false;
        this.pdfRenderingQueue.renderHighestPriority();
    }

    loadPdf = (obj?: any, showPrintButton?): boolean => {
        if (!obj && !this.src) {
            console.error("PdfViewerService: Cannot load pdf, no source or data provided");
            return false;
        }
        !!obj && typeof obj == 'string' && (this.src = obj);
        // Fetch the PDF document from the URL using promises.
        const loadingTask = this.pdfjs.getDocument({
            data: obj.data,
            cMapUrl: window[PDFJS_CONFIG].CMAP_URL,
            cMapPacked: window[PDFJS_CONFIG].CMAP_PACKED
        });
        loadingTask.promise.then(
            (pdf) => {
                this.currentPdf = pdf;
                this.pdfViewer.setDocument(this.currentPdf);
                this.pdfLinkService.setDocument(this.currentPdf, null);
                angular.element('body').css('overflow', 'hidden');
                this.container.addClass('pdf-loaded');
                this.container.find('.close-pdf-button').off('click').on('click', () => {
                    this.clearPdf();
                });

                this.pdfViewer.eventBus.on('pagesloaded', () => {
                    // Calculate scale manually for screens smaller than 1000px
                    const clientWidth = this.container[0].clientWidth;
                    if (clientWidth < 1000) {
                        const containerWidth = clientWidth - (clientWidth * 0.3); // 30% padding
                        const firstPage = this.pdfViewer._pages[0];
                        const viewport = firstPage.viewport.clone({scale: 1});
                        this.pdfViewer.currentScale = containerWidth / viewport.width; // Set calculated scale
                    }
                });

                if (showPrintButton) {
                    const customPrintHandler = (overridPrint: boolean) => {
                        if (!!overridPrint) {
                            $('#printContainer').children().remove();
                            const printOverlay = $('#overlayContainer');
                            if (this.printService) {
                                console.warn('Ignored window.print() because of a pending print job.');
                                if (this.printService) {
                                    this.printService.destroy();
                                    this.printService = null;
                                }
                                this.forceRendering();
                            }
                            ensureOverlay().then(() => {
                                if (this.printService) {
                                    overlayManager.open('printServiceOverlay');
                                }
                            });
                            try {
                                printOverlay.removeClass('hidden')
                                this.pagesOverview = this.pdfViewer.getPagesOverview();
                                const cntnr = document.getElementById('printContainer');
                                const printService = _app.PDFPrintServiceFactory.instance.createPrintService(this.currentPdf, this.pagesOverview, cntnr, null);
                                this.printService = printService;
                                printService.setPrint(print);
                                this.forceRendering();
                                printService.layout();
                            } finally {
                                if (!this.printService) {
                                    console.error('Expected print service to be initialized.');
                                    ensureOverlay().then(function () {
                                        if (overlayManager.active === 'printServiceOverlay') {
                                            overlayManager.close('printServiceOverlay');
                                        }
                                    });
                                    printOverlay.addClass('hidden');
                                } else {
                                    let activeServiceOnEntry = this.printService;
                                    this.printService.renderPages().then(function () {
                                        return activeServiceOnEntry.performPrint();
                                    }).catch(() => {
                                        printOverlay.addClass('hidden');
                                    }).then(() => {
                                        printOverlay.addClass('hidden');
                                    });
                                }

                            }
                        } else {
                            print();
                        }
                    }
                    const print = window.print;
                    window.print = () => customPrintHandler(false);
                    this.container.find('.print-pdf-button').off('click').on('click', () => {
                        customPrintHandler(true);
                    }).show();
                } else {
                    this.container.find('.print-pdf-button').hide();
                }
            }
        );
    }
}

angular.module('eCareVault.PdfViewer').service('PdfViewerService', PdfViewerService);
